/**
 * Journey Builder Page
 *
 * Create and edit learning journeys (curated quiz sequences).
 * Allows users to build structured learning paths.
 */

import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/db/database";
import type { Journey, JourneyQuizReference } from "@/db/types";
import { AppHeader } from "@/components/layout/AppHeader";
import { PageHeader } from "@/components/layout/PageHeader";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  X,
  GripVertical,
  Save,
  ArrowLeft,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { getQuizFlag } from "@/utils/quizMetadata";
import { BookMascot } from "@/components/ui/BookMascot";

export function JourneyBuilderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingJourneyId = searchParams.get("edit");

  // Load data
  const configs = useLiveQuery(() => db.quiz_configs.toArray());
  const existingJourney = useLiveQuery(
    () => (editingJourneyId ? db.journeys.get(editingJourneyId) : undefined),
    [editingJourneyId]
  );

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<
    "phone" | "admin" | "cities" | "custom" | undefined
  >(undefined);
  const [selectedQuizzes, setSelectedQuizzes] = useState<
    JourneyQuizReference[]
  >([]);

  // Initialize form when editing
  useState(() => {
    if (existingJourney) {
      setTitle(existingJourney.title);
      setDescription(existingJourney.description);
      setCategory(existingJourney.category);
      setSelectedQuizzes(existingJourney.quizzes);
    }
  });

  // Available quizzes (not yet in journey)
  const availableQuizzes = useMemo(() => {
    if (!configs) return [];
    const selectedIds = new Set(selectedQuizzes.map((q) => q.quizId));
    return configs.filter((c) => !selectedIds.has(c.signature));
  }, [configs, selectedQuizzes]);

  // Add quiz to journey
  const handleAddQuiz = (quizId: string) => {
    const quiz = configs?.find((c) => c.signature === quizId);
    if (!quiz) return;

    setSelectedQuizzes([
      ...selectedQuizzes,
      {
        quizId,
        order: selectedQuizzes.length + 1,
        isCompleted: false,
      },
    ]);
  };

  // Remove quiz from journey
  const handleRemoveQuiz = (quizId: string) => {
    const newQuizzes = selectedQuizzes
      .filter((q) => q.quizId !== quizId)
      .map((q, index) => ({ ...q, order: index + 1 })); // Renumber
    setSelectedQuizzes(newQuizzes);
  };

  // Move quiz up
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newQuizzes = [...selectedQuizzes];
    const temp = newQuizzes[index];
    if (temp) {
      newQuizzes[index] = newQuizzes[index - 1]!;
      newQuizzes[index - 1] = temp;
    }
    // Renumber
    setSelectedQuizzes(
      newQuizzes.map((q, i) => ({ ...q, order: i + 1 }))
    );
  };

  // Move quiz down
  const handleMoveDown = (index: number) => {
    if (index === selectedQuizzes.length - 1) return;
    const newQuizzes = [...selectedQuizzes];
    const temp = newQuizzes[index];
    if (temp) {
      newQuizzes[index] = newQuizzes[index + 1]!;
      newQuizzes[index + 1] = temp;
    }
    // Renumber
    setSelectedQuizzes(
      newQuizzes.map((q, i) => ({ ...q, order: i + 1 }))
    );
  };

  // Save journey
  const handleSave = async () => {
    // Validation
    if (!title.trim()) {
      toast.error("Please enter a journey title");
      return;
    }

    if (selectedQuizzes.length === 0) {
      toast.error("Please add at least one quiz to the journey");
      return;
    }

    try {
      const now = new Date().toISOString();

      const journey: Journey = {
        id: editingJourneyId || crypto.randomUUID(),
        title: title.trim(),
        description: description.trim(),
        category,
        quizzes: selectedQuizzes,
        isPublished: false,
        createdAt: existingJourney?.createdAt || now,
        lastModifiedAt: now,
      };

      await db.journeys.put(journey);

      toast.success(
        editingJourneyId
          ? "Journey updated successfully!"
          : "Journey created successfully!"
      );

      navigate("/journeys/my-journeys");
    } catch (error) {
      console.error("Error saving journey:", error);
      toast.error("Failed to save journey");
    }
  };

  if (!configs) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader />
        <PageHeader
          title="Journey Builder"
          subtitle="Loading..."
        />
        <div className="flex-1" />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />
      <PageHeader
        title={editingJourneyId ? "Edit Journey" : "Create Journey"}
        subtitle="Build a structured learning path by sequencing quizzes"
      />

      <div className="container mx-auto p-4 md:p-6 max-w-5xl">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/journeys/my-journeys")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to My Journeys
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Left: Journey Settings */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Journey Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Title *
                  </label>
                  <Input
                    placeholder="e.g., Master Western Europe"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Description
                  </label>
                  <Textarea
                    placeholder="Describe what this journey covers..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Category (Optional)
                  </label>
                  <Select
                    value={category}
                    onValueChange={(value) =>
                      setCategory(
                        value as "phone" | "admin" | "cities" | "custom"
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone Area Codes</SelectItem>
                      <SelectItem value="admin">
                        Administrative Divisions
                      </SelectItem>
                      <SelectItem value="cities">Cities</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Add Quiz Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add Quizzes
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Select quizzes to add to your journey
                </p>
              </CardHeader>
              <CardContent>
                {availableQuizzes.length === 0 ? (
                  <div className="text-center py-6 space-y-3">
                    <BookMascot size="md" />
                    <p className="text-sm text-muted-foreground">
                      {configs.length === 0
                        ? "No saved quizzes yet. Create some quizzes first!"
                        : "All your quizzes are already in this journey"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {availableQuizzes.map((quiz) => (
                      <button
                        key={quiz.signature}
                        onClick={() => handleAddQuiz(quiz.signature)}
                        className="w-full text-left p-3 rounded-lg border hover:bg-muted transition-colors"
                      >
                        <div className="font-medium">
                          <span className="mr-2">{getQuizFlag(quiz)}</span>
                          {quiz.title}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {quiz.category} • {quiz.eligibleCount} questions
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {configs.length === 0 && (
                  <Button
                    className="w-full mt-4"
                    onClick={() => navigate("/quiz/studio")}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Your First Quiz
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Quiz Sequence */}
          <Card>
            <CardHeader>
              <CardTitle>Journey Sequence</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedQuizzes.length} quiz
                {selectedQuizzes.length !== 1 ? "zes" : ""} • Drag to reorder
              </p>
            </CardHeader>
            <CardContent>
              {selectedQuizzes.length === 0 ? (
                <div className="text-center py-12 space-y-4">
                  <BookMascot size="lg" />
                  <p className="text-muted-foreground">
                    Add quizzes from the left to build your journey
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedQuizzes.map((ref, index) => {
                    const quiz = configs?.find(
                      (c) => c.signature === ref.quizId
                    );
                    if (!quiz) return null;

                    return (
                      <div
                        key={ref.quizId}
                        className="flex items-center gap-2 p-3 rounded-lg border bg-card"
                      >
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                          >
                            <GripVertical className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMoveDown(index)}
                            disabled={index === selectedQuizzes.length - 1}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                          >
                            <GripVertical className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-semibold flex-shrink-0">
                          {index + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            <span className="mr-2">{getQuizFlag(quiz)}</span>
                            {quiz.title}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {quiz.eligibleCount} questions
                          </div>
                        </div>

                        <button
                          onClick={() => handleRemoveQuiz(ref.quizId)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-6 border-t">
                <Button
                  className="w-full"
                  onClick={handleSave}
                  disabled={!title.trim() || selectedQuizzes.length === 0}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingJourneyId ? "Update Journey" : "Create Journey"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
